import {
  Box,
  Button,
  Collapse,
  Grid,
  HStack,
  Icon,
  Progress,
  Skeleton,
  Stack,
  Text,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import {
  BarChart3,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  GraduationCap,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';

interface UnitStatsProps {
  stats: RouterOutputs['unitsOfCompetency']['getUnitStats'] | undefined;
  isLoading?: boolean;
}

function StatCard({
  title,
  value,
  subtitle,
  icon: IconComponent,
  color = 'blue',
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color?: string;
}) {
  return (
    <Box
      p="4"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      bg="bg.surface"
      h="100%"
    >
      <HStack spacing="3" align="start">
        <Box p="2" borderRadius="md" bg={`${color}.50`} color={`${color}.600`}>
          <Icon as={IconComponent} boxSize="5" />
        </Box>
        <VStack align="start" spacing="1" flex="1">
          <Text fontSize="sm" color="fg.muted" fontWeight="medium">
            {title}
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="fg.default">
            {value}
          </Text>
          {typeof subtitle === 'string' && subtitle !== null ? (
            <Text fontSize="xs" color="fg.muted">
              {subtitle}
            </Text>
          ) : null}
        </VStack>
      </HStack>
    </Box>
  );
}

function ProgressCard({
  title,
  value,
  percentage,
  color = 'blue',
  icon: IconComponent,
}: {
  title: string;
  value: number;
  percentage: number;
  color?: string;
  icon: React.ElementType;
}) {
  return (
    <Box p="4" borderWidth="1px" borderColor="border.default" borderRadius="md" bg="bg.surface">
      <VStack align="start" spacing="3" w="full">
        <HStack spacing="3" w="full">
          <Box p="2" borderRadius="md" bg={`${color}.50`} color={`${color}.600`}>
            <Icon as={IconComponent} boxSize="4" />
          </Box>
          <VStack align="start" spacing="0" flex="1">
            <Text fontSize="sm" color="fg.muted" fontWeight="medium">
              {title}
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="fg.default">
              {value}
            </Text>
          </VStack>
          <Text fontSize="sm" color="fg.muted" fontWeight="bold">
            {percentage.toFixed(1)}%
          </Text>
        </HStack>
        <Progress value={percentage} size="sm" colorScheme={color} borderRadius="full" w="full" />
      </VStack>
    </Box>
  );
}

function CompletionTimeCard({
  title,
  days,
  hours,
  minutes,
  totalCompleted,
  color = 'blue',
  icon: IconComponent,
}: {
  title: string;
  days?: number;
  hours?: number;
  minutes?: number;
  totalCompleted: number;
  color?: string;
  icon: React.ElementType;
}) {
  return (
    <Box p="4" borderWidth="1px" borderColor="border.default" borderRadius="md" bg="bg.surface">
      <HStack spacing="3" align="start">
        <Box p="2" borderRadius="md" bg={`${color}.50`} color={`${color}.600`}>
          <Icon as={IconComponent} boxSize="5" />
        </Box>
        <VStack align="start" spacing="1" flex="1">
          <Text fontSize="sm" color="fg.muted" fontWeight="medium">
            {title}
          </Text>
          <HStack spacing="2" w="full" justify="space-between">
            {typeof days === 'number' && days > 0 ? (
              <VStack align="center" spacing="0" flex="1" alignItems="start">
                <HStack spacing="2">
                  <Text fontSize="2xl" fontWeight="bold" color="fg.default">
                    {days}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t`days`}
                  </Text>
                </HStack>
              </VStack>
            ) : null}

            {typeof hours === 'number' && hours > 0 ? (
              <VStack align="center" spacing="0" flex="1" alignItems="start">
                <HStack spacing="2">
                  <Text fontSize="2xl" fontWeight="bold" color="fg.default">
                    {hours}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t`hours`}
                  </Text>
                </HStack>
              </VStack>
            ) : null}

            {typeof minutes === 'number' && minutes > 0 ? (
              <VStack align="center" spacing="0" flex="1" alignItems="start">
                <HStack spacing="2">
                  <Text fontSize="2xl" fontWeight="bold" color="fg.default">
                    {minutes}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {t`minutes`}
                  </Text>
                </HStack>
              </VStack>
            ) : null}
          </HStack>
          <Text fontSize="xs" color="fg.muted">
            {totalCompleted} {t`completed`}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

export function UnitStatsSection({ stats, isLoading }: UnitStatsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const learningCompletionTime = stats?.averageCompletionTimes.find(
    (item) => item.material_type === 'Learning',
  );
  const assessmentCompletionTime = stats?.averageCompletionTimes.find(
    (item) => item.material_type === 'Assessment',
  );

  const isLoadingStats = isLoading === true || stats == null;

  return (
    <Stack spacing="4">
      <HStack justify="space-between" align="center">
        <Text textStyle="lg" fontWeight="semibold" color="fg.default">
          {t`Unit Analytics`}
        </Text>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          rightIcon={<Icon as={isExpanded ? ChevronUp : ChevronDown} boxSize="4" />}
        >
          {isExpanded ? t`Hide Stats` : t`Show Stats`}
        </Button>
      </HStack>

      <Collapse in={isExpanded} animateOpacity>
        <Box aria-busy={isLoadingStats}>
          <VisuallyHidden aria-live="polite">
            {isLoadingStats ? t`Loading unit analytics…` : t`Unit analytics loaded`}
          </VisuallyHidden>
          {isLoadingStats ? (
            <Stack spacing="4" aria-hidden>
              <Grid templateColumns="repeat(2, 1fr)" gap="4">
                {['total-students', 'completed', 'in-progress', 'withdrawn'].map((key) => (
                  <Skeleton key={key} height="28" borderRadius="md" />
                ))}
              </Grid>
              <Grid
                templateColumns={{
                  base: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                }}
                gap="4"
              >
                {['conversation-length', 'learner-guide', 'assessments'].map((key) => (
                  <Skeleton key={key} height="28" borderRadius="md" />
                ))}
              </Grid>
              <Grid templateColumns="repeat(auto-fit, minmax(350px, 1fr))" gap="4">
                {['learning-time', 'assessment-time'].map((key) => (
                  <Skeleton key={key} height="28" borderRadius="md" />
                ))}
              </Grid>
              <Skeleton height="40" borderRadius="md" />
            </Stack>
          ) : (
            <Stack spacing="4">
              {/* Student Progress Stats */}
              <Grid templateColumns="repeat(2, 1fr)" gap="4">
                <StatCard
                  title={t`Total Students`}
                  value={stats.dropoutSuccessRates.totalStudents}
                  icon={Users}
                  color="gray"
                />
                <ProgressCard
                  title={t`Students Completed`}
                  value={stats.dropoutSuccessRates.studentsCompleted}
                  percentage={stats.dropoutSuccessRates.successRate}
                  color="green"
                  icon={GraduationCap}
                />
                <ProgressCard
                  title={t`Students In Progress`}
                  value={stats.dropoutSuccessRates.studentsStillInProgress}
                  percentage={stats.dropoutSuccessRates.inProgressRate}
                  color="orange"
                  icon={TrendingUp}
                />
                <ProgressCard
                  title={t`Students Withdrawn`}
                  value={stats.dropoutSuccessRates.studentsWithdrawnIncomplete}
                  percentage={stats.dropoutSuccessRates.dropoutRate}
                  color="red"
                  icon={TrendingDown}
                />
              </Grid>

              {/* Content Analytics */}
              <Grid
                templateColumns={{
                  base: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                }}
                gap="4"
              >
                <StatCard
                  title={t`Conversation Content Length`}
                  value={stats.totalContentLength}
                  subtitle={t`Content items in conversations`}
                  icon={BookOpen}
                  color="blue"
                />
                <StatCard
                  title={t`Learner Guide Text Length`}
                  value={stats.totalLearnerGuideContentLength.textContentLength.toLocaleString()}
                  subtitle={`${stats.totalLearnerGuideContentLength.contentItemsCount} ${t`content sections`}`}
                  icon={FileText}
                  color="purple"
                />
                <Box gridColumn={{ base: '1', md: '1 / -1', lg: 'auto' }}>
                  <StatCard
                    title={t`Total Assessments`}
                    value={stats.assessmentBreakdown.total_assessments}
                    subtitle={`${stats.assessmentBreakdown.total_questions} ${t`questions`}`}
                    icon={BarChart3}
                    color="indigo"
                  />
                </Box>
              </Grid>

              {/* Completion Time Analytics */}
              {learningCompletionTime != null || assessmentCompletionTime != null ? (
                <Grid templateColumns="repeat(auto-fit, minmax(350px, 1fr))" gap="4">
                  {learningCompletionTime ? (
                    <CompletionTimeCard
                      title={t`Avg Learning Completion Time`}
                      hours={learningCompletionTime.avg_completion_hours}
                      totalCompleted={learningCompletionTime.total_completed}
                      icon={Clock}
                      color="cyan"
                    />
                  ) : null}

                  {assessmentCompletionTime ? (
                    <CompletionTimeCard
                      title={t`Avg Assessment Completion Time`}
                      hours={assessmentCompletionTime.avg_completion_hours}
                      totalCompleted={assessmentCompletionTime.total_completed}
                      icon={Clock}
                      color="teal"
                    />
                  ) : null}
                </Grid>
              ) : null}

              {/* Assessment Breakdown Details */}
              {Object.keys(stats.assessmentBreakdown.slide_type_breakdown).length > 0 ? (
                <Box
                  p="4"
                  borderWidth="1px"
                  borderColor="border.default"
                  borderRadius="md"
                  bg="bg.surface"
                >
                  <VStack align="start" spacing="3" w="full">
                    <HStack spacing="2">
                      <Icon as={BarChart3} boxSize="5" color="purple.600" />
                      <Text fontSize="md" fontWeight="semibold" color="fg.default">
                        {t`Assessment Content Breakdown`}
                      </Text>
                    </HStack>

                    <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap="3" w="full">
                      {Object.entries(stats.assessmentBreakdown.slide_type_breakdown).map(
                        ([type, count]) => (
                          <VStack key={type} align="start" spacing="1">
                            <Text fontSize="xs" color="fg.muted" textTransform="capitalize">
                              {type.replace(/([A-Z])/g, ' $1').toLowerCase()}
                            </Text>
                            <Text fontSize="2xl" fontWeight="bold" color="fg.default">
                              {count}
                            </Text>
                          </VStack>
                        ),
                      )}
                    </Grid>

                    {Object.keys(stats.assessmentBreakdown.questions_by_type).length > 0 ? (
                      <>
                        <Text fontSize="sm" fontWeight="medium" color="fg.muted" mt="2">
                          {t`Question Types:`}
                        </Text>
                        <Grid
                          templateColumns="repeat(auto-fit, minmax(150px, 1fr))"
                          gap="3"
                          w="full"
                        >
                          {Object.entries(stats.assessmentBreakdown.questions_by_type).map(
                            ([type, count]) => (
                              <VStack key={type} align="start" spacing="1">
                                <Text fontSize="xs" color="fg.muted" textTransform="capitalize">
                                  {type.replace(/_/g, ' ')}
                                </Text>
                                <Text fontSize="2xl" fontWeight="bold" color="fg.default">
                                  {count}
                                </Text>
                              </VStack>
                            ),
                          )}
                        </Grid>
                      </>
                    ) : null}
                  </VStack>
                </Box>
              ) : null}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Stack>
  );
}
